import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useError } from "../../../contexts/ErrorContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useStudents, StudentRollScholarUpdateDto } from "../../../hooks/useStudents";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import RollNumberModal from "../../../components/Modals/RollNumberModal";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// @ts-ignore
import html2pdf from "html2pdf.js";

const formatRollNumber = (roll: string | number | null | undefined) => {
  if (roll === null || roll === undefined || roll === "") return "-";
  const str = String(roll).trim();
  if (/^\d+$/.test(str)) {
    return str.padStart(2, '0');
  }
  return str;
};

const getResponsiveFontSize = (textLength: number, isPrint?: boolean) => {
  if (isPrint) {
    if (textLength <= 10) return "20px";
    if (textLength <= 15) return "16px";
    return "13px";
  } else {
    if (textLength <= 10) return "13px";
    if (textLength <= 15) return "11px";
    return "9px";
  }
};

const RollNumberCard: React.FC<{
  studentName: string;
  rollNumber?: string | number | null;
  isPrint?: boolean;
}> = ({ studentName, rollNumber, isPrint }) => {
  const formattedRoll = formatRollNumber(rollNumber);
  const nameFontSize = getResponsiveFontSize(studentName.length, isPrint);
  const rollFontSize = getResponsiveFontSize(formattedRoll.length, isPrint);

  return (
    <div
      style={{
        border: isPrint ? "2px solid #000000" : "1.5px solid #000000",
        borderRadius: "0px",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        maxWidth: isPrint ? "220px" : "115px",
        maxHeight: isPrint ? "150px" : "90px",
        boxSizing: "border-box",
        padding: isPrint ? "15px" : "8px",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      <div
        style={{
          color: "#000000",
          fontWeight: "500",
          fontSize: nameFontSize,
          textAlign: "center",
          marginBottom: isPrint ? "10px" : "4px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: 1.2,
          width: "100%",
        }}
      >
        {studentName}
      </div>
      <div
        style={{
          color: "#000000",
          fontWeight: "500",
          fontSize: rollFontSize,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
        }}
      >
        {formattedRoll}
      </div>
    </div>
  );
};


const RollNumberManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { user } = useAuth();
  const { selectedSession: contextSession, selectedClass: contextClass } = useSessionClass();
  const [rollNumberModalOpen, setRollNumberModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  
  const printRef = useRef<HTMLDivElement>(null);

  // Get schoolId
  const schoolId = user?.schoolId;

  const { updateRollNumberAndScholarNumberMutation, useStudentsBySessionIdAndClassId } = useStudents(false, schoolId || undefined);

  // Normalize IDs for student hook
  const selectedSessionId = contextSession?.id ?? null;
  const selectedClassId = contextClass?.id ?? null;
  
  const className = contextClass
    ? `${contextClass.name} ${contextClass.section || ""}`.trim()
    : "";
  const sessionYear = contextSession?.name ?? "";

  const { data: filteredStudentsFromAPI = [], isLoading: isLoadingFilteredStudents } = useStudentsBySessionIdAndClassId(
    selectedSessionId ?? undefined,
    selectedClassId ?? undefined
  );

  // Filter and sort active students
  const filteredStudents = useMemo(() => {
    if (!selectedSessionId || !selectedClassId) {
      return [];
    }
    return (filteredStudentsFromAPI || [])
      .filter((student) => student.status !== false)
      .sort((a, b) => {
        const nameA = a.studentName || "";
        const nameB = b.studentName || "";
        return nameA.localeCompare(nameB);
      });
  }, [selectedSessionId, selectedClassId, filteredStudentsFromAPI]);

  const handleOpenRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(true);
  }, []);

  const handleCloseRollNumberModal = useCallback(() => {
    setRollNumberModalOpen(false);
  }, []);

  const handleOpenPreview = useCallback(() => {
    setCurrentPage(0);
    setPreviewOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleNextPage = useCallback(() => {
    const totalPages = Math.ceil(filteredStudents.length / 15);
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  }, [filteredStudents.length]);

  const downloadPdf = useCallback(async () => {
    if (!printRef.current) return;
    setIsDownloadingPdf(true);
    
    try {
      const element = printRef.current;
      const cleanClassName = className.replace(/[^a-zA-Z0-9-_]/g, '_');
      const cleanSession = sessionYear.replace(/[^a-zA-Z0-9-_]/g, '_');
      
      const opt = {
        margin: 0,
        filename: `RollNumbers_${cleanClassName}_${cleanSession}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: {
          unit: "in",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        pagebreak: { mode: "css" }
      };

      await html2pdf().set(opt).from(element).save();
      showSuccess(t("pdf_downloaded_successfully") || "PDF downloaded successfully");
    } catch (error) {
      showError(t("failed_download_pdf") || "Failed to download PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [className, sessionYear, showSuccess, showError, t]);

  const handleSubmitRollNumbers = useCallback(
    async (students: StudentRollScholarUpdateDto[]) => {
      try {
        await updateRollNumberAndScholarNumberMutation.mutateAsync(students);
        showSuccess(
          t("roll_numbers_updated_successfully") || "Roll numbers updated successfully"
        );
        handleCloseRollNumberModal();
      } catch (error: any) {
        showError(
          error?.response?.data?.message ||
            t("error_updating_roll_numbers") ||
            "Error updating roll numbers"
        );
      }
    },
    [updateRollNumberAndScholarNumberMutation, handleCloseRollNumberModal, showError, showSuccess, t]
  );

  // Pagination calculations
  const totalPages = Math.ceil(filteredStudents.length / 15);
  const pageStudents = useMemo(() => {
    return filteredStudents.slice(currentPage * 15, (currentPage + 1) * 15);
  }, [filteredStudents, currentPage]);

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("roll_number_management") || "Roll Number Management"}
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              disabled={filteredStudents.length === 0}
              onClick={handleOpenPreview}
              sx={{
                borderColor: "#3092e3",
                color: "#3092e3",
                "&:hover": { 
                  borderColor: "primary.main",
                  backgroundColor: "rgba(48, 146, 227, 0.04)"
                },
              }}
            >
              {t("download_roll_numbers") || "Get Roll Numbers"}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
              }}
              onClick={handleOpenRollNumberModal}
            >
              {t("add_roll_number") || "Add Roll Number"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <RollNumberModal
        open={rollNumberModalOpen}
        handleClose={handleCloseRollNumberModal}
        onSubmit={handleSubmitRollNumbers}
      />

      {/* Centered Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            backgroundColor: "#f8fafc",
          }
        }}
      >
        {/* Dialog Close Button */}
        <IconButton 
          onClick={handleClosePreview} 
          size="medium" 
          sx={{ 
            position: "absolute",
            top: 8,
            right: 8,
            color: "text.secondary",
            zIndex: 10
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Dialog Content */}
        <DialogContent 
          sx={{ 
            p: 4, 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center" 
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "text.primary", textAlign: "center" }}>
            {t("roll_number_cards_preview") || "Roll Number Cards Preview"}
          </Typography>

          {/* A4 Paper Sheet Preview Container */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "440px",
              height: "622px",
              padding: "24px",
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              boxSizing: "border-box",
              mb: 3
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridTemplateRows: "repeat(5, 1fr)",
                gap: "10px",
                height: "100%",
                width: "100%",
              }}
            >
              {Array.from({ length: 15 }).map((_, slotIdx) => {
                const student = pageStudents[slotIdx];
                if (!student) {
                  return (
                    <div
                      key={`empty-preview-${slotIdx}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                        visibility: "hidden"
                      }}
                    />
                  );
                }
                return (
                  <div
                    key={student.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <RollNumberCard
                      studentName={student.studentName}
                      rollNumber={student.rollNumber}
                      isPrint={false}
                    />
                  </div>
                );
              })}
            </div>
          </Box>

          {/* Navigation Controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={handlePrevPage}
              disabled={currentPage === 0}
              sx={{ borderRadius: 2 }}
            >
              {t("previous") || "Previous"}
            </Button>
            <Typography variant="body2" fontWeight="600" color="text.primary">
              {t("page_x_of_y", { current: currentPage + 1, total: totalPages }) || `Page ${currentPage + 1} of ${totalPages}`}
            </Typography>
            <Button
              variant="outlined"
              endIcon={<ChevronRightIcon />}
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
              sx={{ borderRadius: 2 }}
            >
              {t("next") || "Next"}
            </Button>
          </Box>

          {/* Download Button matching mockup style */}
          <Button
            variant="outlined"
            onClick={downloadPdf}
            disabled={isDownloadingPdf}
            sx={{
              borderColor: "#000000",
              color: "#000000",
              borderRadius: "0px",
              textTransform: "none",
              px: 5,
              py: 1,
              fontSize: "16px",
              fontWeight: "500",
              borderWidth: "1.5px",
              "&:hover": {
                borderColor: "#333333",
                borderWidth: "1.5px",
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
          >
            {isDownloadingPdf ? `${t("downloading") || "Downloading"}...` : t("download") || "Download"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Hidden Off-Screen Container for Print Layout */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <div ref={printRef} style={{ width: "790px", backgroundColor: "#ffffff" }}>
          {Array.from({ length: totalPages }).map((_, pageIdx) => {
            const pageStudentsForPrint = filteredStudents.slice(pageIdx * 15, (pageIdx + 1) * 15);
            return (
              <div 
                key={pageIdx} 
                style={{
                  pageBreakAfter: pageIdx < totalPages - 1 ? "always" : "auto",
                  width: "790px",
                  height: "1120px",
                  padding: "60px 40px",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                {/* 3-column, 5-row grid inside print page */}
                <div 
                  style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr 1fr", 
                    gridTemplateRows: "repeat(5, 1fr)", 
                    gap: "20px", 
                    height: "100%", 
                    width: "100%",
                    alignContent: "center",
                    justifyContent: "center"
                  }}
                >
                  {Array.from({ length: 15 }).map((_, slotIdx) => {
                    const student = pageStudentsForPrint[slotIdx];
                    if (!student) {
                      return (
                        <div 
                          key={`empty-print-${slotIdx}`} 
                          style={{ 
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            width: "100%",
                            visibility: "hidden" 
                          }} 
                        />
                      );
                    }
                    return (
                      <div 
                        key={student.id} 
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          justifyContent: "center", 
                          height: "100%", 
                          width: "100%" 
                        }}
                      >
                        <RollNumberCard
                          studentName={student.studentName}
                          rollNumber={student.rollNumber}
                          isPrint={true}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default RollNumberManagement;
